/**
 * S3 Helper functions
 * */

import {
    AbortMultipartUploadCommand,
    CompleteMultipartUploadCommand,
    CreateMultipartUploadCommand,
    DeleteObjectCommand,
    DeleteObjectsCommand,
    GetObjectCommand,
    GetObjectTaggingCommand,
    HeadObjectCommand,
    ListObjectsV2Command,
    ListObjectVersionsCommand,
    PutObjectCommand,
    PutObjectTaggingCommand,
    S3Client,
    UploadPartCommand,
    type _Object,
    type GetObjectCommandOutput,
    type HeadObjectCommandOutput,
    type ServerSideEncryption,
    type StorageClass,
} from "@aws-sdk/client-s3";
import { getSignedUrl } from "@aws-sdk/s3-request-presigner";

/**
 * Get a File from S3
 * */
export const getObjectBlob = async (
	s3Client: S3Client,
	bucket: string,
	key: string,
): Promise<Blob> => {
	const command = new GetObjectCommand({
		Bucket: bucket,
		Key: key,
	});

	const response = await s3Client.send(command);
	if (!response.Body)
		throw new Error("File not found or empty on the Bucket.");

	return await convertObjectToBlob(response);
};

/**
 * Get head from a S3 file
 * */
export const getObjectHead = async (
	s3Client: S3Client,
	bucket: string,
	key: string,
): Promise<HeadObjectCommandOutput> => {
	const command = new HeadObjectCommand({
		Bucket: bucket,
		Key: key,
	});

	const response = await s3Client.send(command);
	if (!response) throw new Error("File not found on the Bucket.");

	return response;
};

/**
 * Upload a File on S3
 * */
export const uploadFile = async (
	s3Client: S3Client,
	bucket: string,
	key: string,
	file: File,
): Promise<boolean> => {
	try {
		const fileBytes = new Uint8Array(await file.arrayBuffer());

		const command = new PutObjectCommand({
			Bucket: bucket,
			Key: key,
			Body: fileBytes,
			ContentType: file.type,
		});

		const response = await s3Client.send(command);
		if (response.$metadata.httpStatusCode !== 200) return false;

		return true;
	} catch (error) {
		console.error(`Upload File into S3 failed: ${error}`);
		return false;
	}
};

export interface UploadFileOptions {
	/** Optional metadata to attach to the object */
	metadata?: Record<string, string>;
	/** Optional cache control header */
	cacheControl?: string;
	/** Optional content encoding */
	contentEncoding?: string;
	/** Optional content language */
	contentLanguage?: string;
	/** Optional content disposition */
	contentDisposition?: string;
	/** Optional server-side encryption */
	serverSideEncryption?: ServerSideEncryption;
	/** Optional storage class */
	storageClass?: StorageClass;
}

/**
 * Upload a file to S3 with optional tags and metadata
 *
 * @param s3Client - AWS S3 client instance
 * @param bucket - S3 bucket name where the file will be uploaded
 * @param key - S3 object key (file path/name in the bucket)
 * @param file - File object to upload
 * @param tags - Optional key-value pairs of tags to attach to the object
 * @param options - Optional upload configuration (metadata, cache control, etc.)
 *
 * @returns Promise<boolean> - Returns true if upload successful, false otherwise
 *
 * @example
 * ```typescript
 * // Upload file with permission tags
 * const success = await uploadFileWithTags(
 *   s3Client,
 *   'help-files-bucket',
 *   'documents/user-guide.pdf',
 *   fileObject,
 *   {
 *     'TechSupport': 'allow',
 *     'Engineering': 'allow',
 *     'description': 'User guide for system setup'
 *   }
 * );
 *
 * // Upload file without tags
 * const success = await uploadFileWithTags(
 *   s3Client,
 *   'my-bucket',
 *   'files/document.pdf',
 *   fileObject
 * );
 *
 * // Upload with tags and metadata
 * const success = await uploadFileWithTags(
 *   s3Client,
 *   'my-bucket',
 *   'files/document.pdf',
 *   fileObject,
 *   { 'TechSupport': 'allow', 'version': '1.0' },
 *   {
 *     metadata: { 'author': 'John Doe', 'department': 'Engineering' },
 *     cacheControl: 'max-age=3600'
 *   }
 * );
 * ```
 *
 * @throws {Error} When upload fails or S3 API calls fail
 */
export const uploadFileWithTags = async (
	s3Client: S3Client,
	bucket: string,
	key: string,
	file?: File | undefined,
	tags?: Record<string, string>,
	options: UploadFileOptions = {},
): Promise<boolean> => {
	try {
		if (file) {
			const fileBytes = new Uint8Array(await file.arrayBuffer());

			// Prepare the upload command
			const uploadCommand = new PutObjectCommand({
				Bucket: bucket,
				Key: key,
				Body: fileBytes,
				ContentType: file?.type || "application/octet-stream",
				Metadata: options.metadata,
				CacheControl: options.cacheControl,
				ContentEncoding: options.contentEncoding,
				ContentLanguage: options.contentLanguage,
				ContentDisposition: options.contentDisposition,
				ServerSideEncryption: options.serverSideEncryption,
				StorageClass: options.storageClass,
			});

			// Upload the file
			const uploadResponse = await s3Client.send(uploadCommand);

			if (uploadResponse.$metadata.httpStatusCode !== 200) {
				console.error(
					`Upload failed with status: ${uploadResponse.$metadata.httpStatusCode}`,
				);
				return false;
			}
		}

		// If tags are provided, add them to the uploaded object
		if (tags && Object.keys(tags).length > 0) {
			const tagSet = Object.entries(tags).map(([key, value]) => ({
				Key: key,
				Value: value,
			}));

			const taggingCommand = new PutObjectTaggingCommand({
				Bucket: bucket,
				Key: key,
				Tagging: {
					TagSet: tagSet,
				},
			});

			const taggingResponse = await s3Client.send(taggingCommand);

			if (taggingResponse.$metadata.httpStatusCode !== 200) {
				console.error(
					`Tagging failed with status: ${taggingResponse.$metadata.httpStatusCode}`,
				);
				// Note: File is uploaded but tagging failed
				console.warn(
					`File uploaded successfully but tagging failed for: ${key}`,
				);
			}
		}

		console.log(`✅ File uploaded successfully: ${key}`);
		return true;
	} catch (error) {
		console.error(`❌ Upload failed for ${key}:`, error);
		return false;
	}
};

export const convertObjectToStream = (response: GetObjectCommandOutput) => {
	return response.Body as ReadableStream;
};

/**
 * Convert S3 Object to Blob
 * */
export const convertObjectToBlob = async (response: GetObjectCommandOutput) => {
	const bodyStream = response.Body as ReadableStream<Uint8Array>;

	if (!(bodyStream && typeof bodyStream.getReader === "function")) {
		throw new Error("S3 Body is not a valid ReadableStream");
	}

	const reader = bodyStream.getReader();
	const chunks: Uint8Array[] = [];

	// eslint-disable-next-line no-constant-condition
	while (true) {
		const { done, value } = await reader.read();
		if (done) break;
		if (value) chunks.push(value);
	}

	return new Blob(chunks as BlobPart[]);
};

interface PresignedUrlOptions {
	expiresIn?: number; // seconds
	responseContentDisposition?: string; // force download
	responseContentType?: string;
}

/**
 * Create a presigned URL for downloading an object from S3
 *
 * @param s3Client - AWS S3 client instance
 * @param bucket - S3 bucket name
 * @param key - Object key in the bucket
 * @param options - Optional parameters for the presigned URL
 * @returns Promise<string> - Presigned URL for downloading the object
 */
export async function createPresignedDownloadUrl(
	s3Client: S3Client,
	bucket: string,
	key: string,
	options: PresignedUrlOptions = {},
): Promise<string> {
	const {
		expiresIn = 3600, // 1 hour default
		responseContentDisposition,
		responseContentType,
	} = options;

	try {
		// Check if the object exists
		await s3Client.send(
			new HeadObjectCommand({
				Bucket: bucket,
				Key: key,
			}),
		);

		const command = new GetObjectCommand({
			Bucket: bucket,
			Key: key,
			ResponseContentDisposition: responseContentDisposition,
			ResponseContentType: responseContentType,
		});

		const signedUrl = await getSignedUrl(s3Client, command, {
			expiresIn,
		});

		return signedUrl;
	} catch (error) {
		console.error(`Error creating presigned URL for ${key}:`, error);
		throw new Error(`Failed to create download link for ${key}`);
	}
}

export interface ObjectVersion {
	versionId?: string;
	lastModified?: Date;
	isLatest: boolean;
	size?: number;
	versionNumber: number;
}

export interface AccessibleObject {
	key: string;
	size?: number;
	lastModified?: Date;
	allowedGroups: string[];
	metadata?: Record<string, string>;
	allTags?: Record<string, string>;
	targetTagValue?: string;
	versionId?: string;
	etag?: string;
	isLatestVersion?: boolean;
	currentVersionNumber?: number;
	totalVersions?: number;
	versions?: ObjectVersion[];
}

export interface FilteredObject extends _Object {
	allowedGroups?: string[];
}

/**
 * Tag groups and their associated cognito user groups
 * This is used to determine which user groups can access specific S3 objects based on their tags.
 * Necessary because the S3 Tag are limited to 10 tags per object.
 */
export const TAG_GROUP_DEFINITIONS: Record<string, string[]> = {
	Organization: ["AccountOwner", "Manager", "Operator"],
	Dealer: ["DealerOwner", "DealerUser"],
	FlightPathAdmin: ["FlightPathAdmin"],
	TechSupport: ["TechSupport"],
	Engineering: ["Engineering"],
	Sales: ["Sales"],
};

/**
 * Get the S3 tag group for a given user group
 */
export const getUserGroupTag = (userGroup: string): string => {
	// Find which tag group contains this user group
	for (const [tagGroup, userGroups] of Object.entries(
		TAG_GROUP_DEFINITIONS,
	)) {
		if (userGroups.includes(userGroup)) {
			return tagGroup;
		}
	}
	// If not found in any group, return the user group itself (one-to-one mapping)
	return userGroup;
};

/**
 * Get all user groups that have access via a specific S3 tag
 */
export const getUserGroupsFromTag = (tagGroup: string): string[] => {
	return TAG_GROUP_DEFINITIONS[tagGroup] || [tagGroup];
};

/**
 * Convert user groups to S3 tag groups (removes duplicates)
 */
export const convertUserGroupsToTagGroups = (
	userGroups: string[],
): string[] => {
	const tagGroups = new Set<string>();

	userGroups.forEach(userGroup => {
		const tagGroup = getUserGroupTag(userGroup);
		tagGroups.add(tagGroup);
	});

	return Array.from(tagGroups);
};

/**
 * Convert S3 tag groups back to all possible user groups
 */
export const convertTagGroupsToUserGroups = (tagGroups: string[]): string[] => {
	const userGroups = new Set<string>();

	tagGroups.forEach(tagGroup => {
		const groups = getUserGroupsFromTag(tagGroup);
		groups.forEach(group => userGroups.add(group));
	});

	return Array.from(userGroups);
};

/**
 * Get all available tag groups
 */
export const getAllTagGroups = (): string[] => {
	return Object.keys(TAG_GROUP_DEFINITIONS);
};

/**
 * Get all available user groups
 */
export const getAllUserGroups = (): string[] => {
	const userGroups = new Set<string>();
	Object.values(TAG_GROUP_DEFINITIONS).forEach(groups => {
		groups.forEach(group => userGroups.add(group));
	});
	return Array.from(userGroups).sort();
};

/**
 * Helper function to list all objects (handles pagination)
 */
export const listAllObjects = async (
	s3Client: S3Client,
	bucket: string,
	prefix?: string,
): Promise<_Object[]> => {
	const allObjects: _Object[] = [];
	let continuationToken: string | undefined;

	do {
		const command = new ListObjectsV2Command({
			Bucket: bucket,
			Prefix: prefix,
			ContinuationToken: continuationToken,
		});

		const response = await s3Client.send(command);
		if (!response)
			throw new Error("Failed to list objects from the bucket.");

		if (response.Contents) {
			allObjects.push(...response.Contents);
		}

		continuationToken = response.NextContinuationToken;
	} while (continuationToken);

	return allObjects;
};

/**
 * Get all versions for specific objects
 */
const getObjectVersions = async (
	s3Client: S3Client,
	bucket: string,
	keys: string[],
): Promise<Record<string, Array<ObjectVersion>>> => {
	const versionsMap: Record<string, Array<ObjectVersion>> = {};

	try {
		let continuationToken: string | undefined;

		do {
			const command = new ListObjectVersionsCommand({
				Bucket: bucket,
				KeyMarker: continuationToken,
			});

			const response = await s3Client.send(command);

			// Process versions
			response.Versions?.forEach(version => {
				if (version.Key && keys.includes(version.Key)) {
					if (!versionsMap[version.Key]) {
						versionsMap[version.Key] = [];
					}

					versionsMap[version.Key].push({
						versionId: version.VersionId,
						lastModified: version.LastModified,
						isLatest: version.IsLatest || false,
						size: version.Size,
						versionNumber: -1, // Indicates "not set yet"
					});
				}
			});

			continuationToken = response.NextKeyMarker;
		} while (continuationToken);
	} catch (error) {
		console.error("Error getting object versions:", error);
	}

	return versionsMap;
};

/**
 * Retrieves S3 objects that are accessible to specific user groups based on object tags.
 *
 * This function filters S3 objects by checking if they have tags matching user group names.
 * Each user group is represented as a separate tag with 'allow' value on the object.
 *
 * @param s3Client - AWS S3 client instance for making API calls
 * @param bucket - S3 bucket name to search for objects
 * @param userGroups - Array of user group names to check access for (e.g., ['TechSupport', 'Sales'])
 * @param includeVersions - Optional flag to include version history for each accessible object
 * @param prefix - Optional prefix to filter objects (acts like a folder path)
 *
 * @returns Promise<AccessibleObject[]> - Array of objects the user groups can access, including:
 *   - Object metadata (key, size, last modified date)
 *   - All object tags as key-value pairs
 *   - Group access information
 *   - Optional version history with numbered versions
 *
 * @example
 * ```typescript
 * // Find files accessible to TechSupport group
 * // Objects should be tagged: TechSupport=allow, Sales=allow, etc.
 * const files = await listObjectsForUserGroups(
 *   s3Client,
 *   'help-files-bucket',
 *   ['TechSupport'],
 *   false
 * );
 *
 * // Find files with version history
 * const filesWithVersions = await listObjectsForUserGroups(
 *   s3Client,
 *   'help-files-bucket',
 *   ['TechSupport', 'Sales'],
 *   true,
 *   'documents/'
 * );
 * ```
 *
 * @throws {Error} When S3 API calls fail or access is denied
 *
 * @remarks
 * - Requires s3:ListBucket, s3:GetObjectTagging, and s3:HeadObject permissions
 * - Objects must have tags like: TechSupport=allow, Sales=allow for access
 * - Objects without matching group tags are automatically excluded
 * - Performance scales with batch processing (5 objects per batch)
 */
export const listObjectsForUserGroups = async (
	s3Client: S3Client,
	bucket: string,
	userGroups: string[],
	includeVersions: boolean = false,
	prefix?: string,
): Promise<AccessibleObject[]> => {
	// Convert user groups to S3 tag groups
	const tagGroupsToCheck = convertUserGroupsToTagGroups(userGroups);

	console.log(`🔍 User groups: [${userGroups.join(", ")}]`);
	console.log(`🏷️  Checking S3 tags: [${tagGroupsToCheck.join(", ")}]`);

	const allObjects = await listAllObjects(s3Client, bucket, prefix);

	const accessibleObjects: AccessibleObject[] = [];
	const batchSize = 5;

	for (let i = 0; i < allObjects.length; i += batchSize) {
		const batch = allObjects.slice(i, i + batchSize);

		const batchPromises = batch.map(async obj => {
			if (!obj.Key) return null;

			try {
				const [headResult, tagsResult] = await Promise.allSettled([
					getObjectHead(s3Client, bucket, obj.Key),
					s3Client.send(
						new GetObjectTaggingCommand({
							Bucket: bucket,
							Key: obj.Key,
						}),
					),
				]);

				if (tagsResult.status !== "fulfilled") {
					return null;
				}

				// Convert all tags to key-value object
				const allTags: Record<string, string> = {};
				tagsResult.value.TagSet?.forEach(tag => {
					if (tag.Key && tag.Value) {
						allTags[tag.Key] = tag.Value;
					}
				});

				// Check if user has access by looking for tag group matches
				const matchingTagGroups: string[] = [];
				const hasAccess = tagGroupsToCheck.some(tagGroup => {
					const groupTag = tagsResult.value.TagSet?.find(
						tag => tag.Key === tagGroup && tag.Value === "allow",
					);
					if (groupTag) {
						matchingTagGroups.push(tagGroup);
						return true;
					}
					return false;
				});

				if (!hasAccess) return null;

				// Also collect all tag groups that have access (for comprehensive display)
				const allAllowedTagGroups: string[] = [];
				tagsResult.value.TagSet?.forEach(tag => {
					if (tag.Value === "allow" && tag.Key) {
						allAllowedTagGroups.push(tag.Key);
					}
				});

				// Convert all allowed tag groups to user groups
				const allAllowedUserGroups =
					convertTagGroupsToUserGroups(allAllowedTagGroups);

				// Get additional object information
				const headData =
					headResult.status === "fulfilled" ? headResult.value : null;

				const result: AccessibleObject = {
					key: obj.Key,
					size: obj.Size,
					lastModified: obj.LastModified,
					allowedGroups: allAllowedUserGroups, // All user groups that have access
					metadata: headData?.Metadata,
					allTags,
					targetTagValue: allAllowedUserGroups.join(", "), // Display user groups, not tag groups
					versionId: headData?.VersionId,
					etag: obj.ETag || headData?.ETag,
				};

				return result;
			} catch (error) {
				console.error(`💥 Error processing ${obj.Key}:`, error);
				return null;
			}
		});

		const batchResults = await Promise.allSettled(batchPromises);

		batchResults.forEach(result => {
			if (result.status === "fulfilled" && result.value !== null) {
				accessibleObjects.push(result.value);
			}
		});
	}

	// Get version information for accessible objects if requested
	if (includeVersions && accessibleObjects.length > 0) {
		const keys = accessibleObjects.map(obj => obj.key);
		const versionsMap = await getObjectVersions(s3Client, bucket, keys);

		// Add version information to each object
		accessibleObjects.forEach(obj => {
			const versions = versionsMap[obj.key];
			if (versions && versions.length > 0) {
				//Sort versions by date (oldest first) and assign version numbers
				const sortedVersions = versions
					.sort(
						(a, b) =>
							(a.lastModified?.getTime() || 0) -
							(b.lastModified?.getTime() || 0),
					)
					.map((version, index) => ({
						...version,
						versionNumber: index + 1,
					}));

				obj.versions = sortedVersions;
				obj.totalVersions = sortedVersions.length;

				// Find the latest version and its number
				const latestVersion = sortedVersions.find(v => v.isLatest);
				if (latestVersion) {
					obj.versionId = latestVersion.versionId;
					obj.isLatestVersion = true;
					obj.currentVersionNumber = latestVersion.versionNumber;
				} else {
					// If no version is marked as latest, assume the newest one is latest
					const newestVersion =
						sortedVersions[sortedVersions.length - 1];
					obj.currentVersionNumber = newestVersion.versionNumber;
				}
			}
		});
	}

	console.log(`🎉 Found: ${accessibleObjects.length} accessible objects`);

	return accessibleObjects;
};

/**
 * Delete a single S3 object (current version only)
 *
 * @param s3Client - AWS S3 client instance
 * @param bucket - S3 bucket name
 * @param key - S3 object key to delete
 * @returns Promise<boolean> - Returns true if deletion was successful, false otherwise
 */
export const deleteS3Object = async (
	s3Client: S3Client,
	bucket: string,
	key: string,
): Promise<boolean> => {
	try {
		const command = new DeleteObjectCommand({
			Bucket: bucket,
			Key: key,
		});

		const response = await s3Client.send(command);

		if (
			response.$metadata.httpStatusCode === 204 ||
			response.$metadata.httpStatusCode === 200
		) {
			console.log(`✅ Object deleted successfully: ${key}`);
			return true;
		} else {
			console.error(
				`Delete failed with status: ${response.$metadata.httpStatusCode}`,
			);
			return false;
		}
	} catch (error) {
		console.error(`❌ Delete failed for ${key}:`, error);
		return false;
	}
};

/**
 * Delete S3 object and ALL its versions (complete removal)
 * This is useful for versioned buckets where you want to completely remove an object
 *
 * @param s3Client - AWS S3 client instance
 * @param bucket - S3 bucket name
 * @param key - S3 object key to delete completely
 * @returns Promise<boolean> - Returns true if all versions were deleted successfully
 */
export const deleteS3ObjectCompletely = async (
	s3Client: S3Client,
	bucket: string,
	key: string,
): Promise<boolean> => {
	try {
		// First, get all versions of the object
		const listCommand = new ListObjectVersionsCommand({
			Bucket: bucket,
			Prefix: key,
		});

		const listResponse = await s3Client.send(listCommand);

		// Collect all versions and delete markers for this specific key
		const objectsToDelete: Array<{ Key: string; VersionId?: string }> = [];

		// Add all versions
		listResponse.Versions?.forEach(version => {
			if (version.Key === key && version.VersionId) {
				objectsToDelete.push({
					Key: key,
					VersionId: version.VersionId,
				});
			}
		});

		// Add all delete markers
		listResponse.DeleteMarkers?.forEach(deleteMarker => {
			if (deleteMarker.Key === key && deleteMarker.VersionId) {
				objectsToDelete.push({
					Key: key,
					VersionId: deleteMarker.VersionId,
				});
			}
		});

		if (objectsToDelete.length === 0) {
			// Object doesn't exist or no versions found, try simple delete
			return await deleteS3Object(s3Client, bucket, key);
		}

		// Delete all versions in batches (S3 allows max 1000 objects per batch)
		const batchSize = 1000;
		let allDeleted = true;

		for (let i = 0; i < objectsToDelete.length; i += batchSize) {
			const batch = objectsToDelete.slice(i, i + batchSize);

			const deleteCommand = new DeleteObjectsCommand({
				Bucket: bucket,
				Delete: {
					Objects: batch,
					Quiet: true, // Don't return info about successful deletions
				},
			});

			const deleteResponse = await s3Client.send(deleteCommand);

			// Check for errors
			if (deleteResponse.Errors && deleteResponse.Errors.length > 0) {
				console.error(
					`Some versions failed to delete for ${key}:`,
					deleteResponse.Errors,
				);
				allDeleted = false;
			}
		}

		if (allDeleted) {
			console.log(
				`✅ Object and all versions deleted completely: ${key} (${objectsToDelete.length} versions)`,
			);
			return true;
		} else {
			console.warn(
				`⚠️ Object ${key} partially deleted, some versions may remain`,
			);
			return false;
		}
	} catch (error) {
		console.error(`❌ Complete delete failed for ${key}:`, error);
		return false;
	}
};

export const uploadMultipartFileWithTags = async (
	s3Client: S3Client,
	bucket: string,
	key: string,
	file?: File | undefined,
	tags?: Record<string, string>,
	options: UploadFileOptions = {},
): Promise<boolean> => {
	console.log(`Starting multipart upload with file size ${file?.size} bytes`);
	if (!file) {
		console.error(`No file provided for multipart upload: ${key}`);
		return false;
	}

	let uploadId: string | undefined;
	try {
		const PART_SIZE = 500 * 1024 * 1024; // 500MB
		//  Create multipart upload
		const createResponse = await s3Client.send(
			new CreateMultipartUploadCommand({
				Bucket: bucket,
				Key: key,
				ContentType: file.type,
				Metadata: options.metadata,
			}),
		);
		uploadId = createResponse.UploadId;

		if (!uploadId) {
			console.error(
				`Failed to create multipart upload for ${key}: No UploadId returned`,
			);
			return false;
		}

		//  Upload parts
		const partCount = Math.ceil(file.size / PART_SIZE);
		const parts = [];
		for (let partNumber = 1; partNumber <= partCount; partNumber++) {
			const start = (partNumber - 1) * PART_SIZE;
			const end = Math.min(start + PART_SIZE, file.size);
			const partBlob = file.slice(start, end);
			const partBytes = new Uint8Array(await partBlob.arrayBuffer());

			const uploadPartPromise = await s3Client.send(
				new UploadPartCommand({
					Bucket: bucket,
					Key: key,
					PartNumber: partNumber,
					UploadId: uploadId,
					Body: partBytes,
				}),
			);

			parts.push({
				ETag: uploadPartPromise.ETag,
				PartNumber: partNumber,
			});
		}
		await s3Client.send(
			new CompleteMultipartUploadCommand({
				Bucket: bucket,
				Key: key,
				UploadId: uploadId,
				MultipartUpload: {
					Parts: parts,
				},
			}),
		);

		console.log(`Multipart upload completed for ${key}`);

		// tags after successful upload
		if (tags && Object.keys(tags).length > 0) {
			const tagSet = Object.entries(tags).map(([key, value]) => ({
				Key: key,
				Value: value,
			}));

			await s3Client.send(
				new PutObjectTaggingCommand({
					Bucket: bucket,
					Key: key,
					Tagging: {
						TagSet: tagSet,
					},
				}),
			);

			console.log(`Tags added successfully for ${key}`);
		}
		return true;
	} catch (error) {
		if (uploadId) {
			// Abort multipart upload on error
			await s3Client.send(
				new AbortMultipartUploadCommand({
					Bucket: bucket,
					Key: key,
					UploadId: uploadId,
				}),
			);
			console.error(
				`Multipart upload aborted for ${key} due to error:`,
				error,
			);
		}
		return false;
	}
};
